import { Avatar, Breakpoints, Colors } from '@helloself/hello-self-ui';
import { Auth } from 'aws-amplify';
import { useSelector } from 'react-redux';
import { Link as RouterLink, useHistory, useLocation } from 'react-router-dom';

import { recordEvent, recordEventWithPath } from '../../../analytics/product';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { Button, Dropdown, DropdownItem, IconHome, IconMessage, IconPeople } from '../../../ui';
import { selectIsAuthenticated, selectUser } from '../../auth/selectors';
import { logoutAndClearCaches as authenticationLogout } from '../../auth/slice';
import { selectUnreadMessagesCount } from '../../messaging/model/selectors';
import { useGetProfilePhotoQuery } from '../../profile/model/profile';
import { selectFirstCamhsChild, selectIsParent, selectMyConnectionsFeatureFlag } from '../../user/selectors';
import { logout as userLogout } from '../../user/slice';
import Logo from '../Logo';
import { NavLink } from './NavLink';
import { useState } from 'react';
import { ConsentSettings } from '../../camhsConsent/ConsentSettings';

export const Header = () => {
    const location = useLocation();
    const history = useHistory();

    const isAuthenticated = useAppSelector(selectIsAuthenticated);
    const user = useAppSelector(selectUser);

    const unreadMessages = useAppSelector(selectUnreadMessagesCount);

    const isConnections = useSelector(selectMyConnectionsFeatureFlag);
    const isParent = useAppSelector(selectIsParent);
    const camhsChild = useAppSelector(selectFirstCamhsChild);
    const isLogin = location.pathname === '/login';

    const dispatch = useAppDispatch();

    const [isConsentSettingsOpen, setIsConsentSettingsOpen] = useState(false);

    /* PUBLIC ROUTES */
    const handleSignUpClick = () => {
        recordEvent({
            name: 'Clicked Sign up',
            attributes: {
                path: location.pathname,
                category: 'Header',
            },
        });
        history.push('/register');
    };

    const handleSignInClick = () => {
        recordEvent({
            name: 'Clicked Sign in',
            attributes: {
                path: location.pathname,
                category: 'Header',
            },
        });
        history.push('/login');
    };

    const handleReferNowClick = (pushPath: string) => {
        recordEventWithPath('Clicked Make a New Referral', location.pathname, {
            category: 'Referrals',
            'Clicked from Location': 'Top Navigation',
        });
        // new event (preferred)
        recordEventWithPath('Clicked Refer Now', location.pathname, {
            category: 'Referrals',
            'Clicked from Location': 'Top Navigation',
        });
        history.push(pushPath);
    };

    /* PRIVATE ROUTES */

    const handleLogoClick = () => {
        recordEventWithPath('Clicked HelloSelf Logo', location.pathname, {
            category: 'Overview',
            'Clicked from Location': 'Top Navigation',
        });
    };

    const handleHomeClick = () => {
        recordEventWithPath('Clicked Home', location.pathname, {
            category: 'Overview',
            'Clicked from Location': 'Top Navigation',
        });
    };

    const handleNetworkClick = () => {
        recordEventWithPath('Clicked Network', location.pathname, {
            category: 'Network',
            'Clicked from Location': 'Top Navigation',
        });
    };

    const handleMessagesClick = () => {
        recordEventWithPath('Clicked Messages', location.pathname, {
            category: 'Messaging',
            Notifications: unreadMessages > 0 ? unreadMessages : 'None',
            'Clicked from Location': 'Top Navigation',
        });
    };

    const handleProfileAvatarClick = () => {
        recordEventWithPath('Clicked Profile Avatar', location.pathname, {
            category: 'Account',
            'Clicked from Location': 'Top Navigation',
        });
    };

    const handleProfileClick = () => {
        recordEventWithPath('Clicked Profile', location.pathname, {
            category: 'Profile',
            'Clicked from Location': 'Top Navigation',
        });
        history.push(`/profile/${user.cognitoId}`);
    };

    const handleCommunityGuidelinesClick = () => {
        recordEventWithPath('Clicked Community Guidelines', location.pathname, {
            category: 'Community Guidelines',
            'Clicked from Location': 'Top Navigation',
        });
        history.push(`/community-guidelines`);
    };

    const handleLogoutClick = async () => {
        await Auth.signOut();
        dispatch(authenticationLogout());
        dispatch(userLogout());
        recordEventWithPath('Clicked Log Out', location.pathname, {
            category: 'Account',
            'Clicked from Location': 'Top Navigation',
        });
    };

    const { data: profilePicture } = useGetProfilePhotoQuery(user.cognitoId, { skip: !user.cognitoId });

    return (
        <header className="sticky top-0 left-0 right-0 w-full z-[100] bg-green-700 text-white">
            {/* calc(-100vh+1px) keeps it within the rendering zone. Otherwise it doesn't work. Dumb I know. */}
            <div className="absolute h-screen top-[calc(-100vh+1px)] left-0 right-0 bg-green-700" />
            {/* If you're changing this height, be wary some containers use it, take a look at Messaging.tsx Line 94 */}
            <div className="flex items-center justify-between px-5 md:px-10 h-16">
                <div className="flex items-center h-full space-x-5">
                    <RouterLink
                        className="logo"
                        style={{ display: 'flex' }}
                        to={isAuthenticated ? '/dashboard' : '/login'}
                        onClick={handleLogoClick}
                    >
                        <Logo />
                    </RouterLink>
                    {isAuthenticated && isConnections && (
                        <nav className="h-full flex items-center">
                            <ul className="list-none flex space-x-4 h-full items-center">
                                <NavLink label="Home" to="/dashboard" icon={<IconHome />} onClick={handleHomeClick} />
                                {!isParent && (
                                    <NavLink
                                        label="Network"
                                        to="/network?tab=search"
                                        icon={<IconPeople />}
                                        onClick={handleNetworkClick}
                                    />
                                )}
                                <NavLink
                                    label="Messages"
                                    to="/messages"
                                    badge={unreadMessages > 0 ? unreadMessages : undefined}
                                    icon={<IconMessage />}
                                    onClick={handleMessagesClick}
                                />
                            </ul>
                        </nav>
                    )}
                </div>
                <div className="flex items-center relative gap-4">
                    {isAuthenticated ? (
                        <>
                            {!isParent && (
                                <Button
                                    inverted={true}
                                    onClick={() => handleReferNowClick('/new-referral')}
                                    size="small"
                                >
                                    <span>Refer now</span>
                                </Button>
                            )}

                            <div className="relative h-[32px] group">
                                <Dropdown>
                                    <Dropdown.Button onClick={handleProfileAvatarClick}>
                                        <Avatar
                                            className=" group-hover:bg-green-200"
                                            size="32px"
                                            profile={{
                                                firstName: user.firstName,
                                                lastName: user.lastName,
                                                profilePicture,
                                            }}
                                            backgroundColor={Colors.MemberGreen100}
                                            textColor={Colors.black}
                                            fontSize="16px"
                                        />
                                        <div className="absolute inset-0 border-2 border-green-200 opacity-0 group-hover:opacity-100 transition-opacity duration-150 ease-in-out rounded-full"></div>
                                    </Dropdown.Button>
                                    <Dropdown.Menu>
                                        {isConnections && !isParent && (
                                            <>
                                                <DropdownItem onClick={() => handleProfileClick()}>
                                                    My profile
                                                </DropdownItem>
                                                <DropdownItem onClick={() => handleCommunityGuidelinesClick()}>
                                                    Community Guidelines
                                                </DropdownItem>
                                            </>
                                        )}
                                        {isParent && camhsChild?.treatmentConsentGivenAt && (
                                            <DropdownItem onClick={() => setIsConsentSettingsOpen(true)}>
                                                Consent settings
                                            </DropdownItem>
                                        )}
                                        <DropdownItem onClick={handleLogoutClick}>Log out</DropdownItem>
                                    </Dropdown.Menu>
                                </Dropdown>
                            </div>
                        </>
                    ) : (
                        <>
                            <Button
                                // We could reset windowWidth when you resize. But IMO it's not normal user behaviour and doesn't really matter.
                                variant={window.innerWidth >= Breakpoints.tablet ? 'secondary' : 'tertiary'}
                                type="button"
                                onClick={isLogin ? handleSignUpClick : handleSignInClick}
                                size="small"
                            >
                                <span className="text-white">{isLogin ? 'Sign up' : 'Sign in'}</span>
                            </Button>
                            <Button
                                variant={window.innerWidth >= Breakpoints.tablet ? 'primary' : 'tertiary'}
                                type="button"
                                onClick={() => handleReferNowClick('/refer')}
                                size="small"
                            >
                                <span className="text-white">Refer now</span>
                            </Button>
                        </>
                    )}
                </div>
            </div>
            {isConsentSettingsOpen && <ConsentSettings onClose={() => setIsConsentSettingsOpen(false)} />}
        </header>
    );
};
